import React, { SetStateAction, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import { Pagination } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import { Severity } from "node-library/dist/constants";
import {
	ClientFilterOption,
	ClientOrganizationSortColumn,
	SortOrder,
} from "node-library/dist/paginations";
import {
	FrontendCompanyWithAdditionalInfomation,
	FrontendOrganization,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";
import { BlackButton } from "../../../common-components/button";
import {
	CustomRadioGroup,
	CustomSearchBar,
	RowDropdown,
} from "../../../common-components/inputs";
import { SortArrowIcon } from "../../../common-components/svgIcons";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import {
	getClientFilterOptionString,
	getClientOrganizationSortString,
} from "../../../helpers/clientHelper";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../requestHelper/sendRequest";
import ViewOrderPageSkeletonUI from "../viewOrders/skeletonUI/orderHistoryPageSkeleton";
import AddOrEditOrganizationModal from "./modal/addOrEditOrganizationModal";
import OrgDetailsContent from "./orgDetailsContent";
import OrgsContent from "./orgsContent";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo | undefined;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
	gotoViewOrderPage: () => void;
}

export default function ClientOrganizationPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const setUnsavedChanges = props.setUnsavedChanges;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const [clientOrganizations, setClientOrganizations] = useState<
		Array<FrontendOrganization>
	>([]);
	const [salesReps, setSalesReps] = useState<Array<FrontendUserInfo>>([]);

	const [selectedOrganization, setSelectedOrganization] =
		useState<FrontendOrganization>();

	const [loading, setLoading] = useState<boolean>(false);
	const [adminInAction, setAdminInAction] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");

	//paginations
	const [totalPageCount, setTotalPageCount] = useState<number>(0);
	const [sortBy, setSortBy] = useState<ClientOrganizationSortColumn>(
		ClientOrganizationSortColumn.ASSOCIATION_ID,
	);
	const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);

	const [currentPagination, setCurrentPagination] = useState<number>(1);

	const [filterOption, setFilterOption] = useState<ClientFilterOption | "">("");

	const [addEditOrgModalOpen, setAddEditOrgModalOpen] =
		useState<boolean>(false);

	//confirm modals
	const [deleteOrgConfirmOpen, setDeleteOrgConfirmOpen] =
		useState<boolean>(false);
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {},
	);

	function toggleSortOrder() {
		if (sortOrder === SortOrder.ASC) {
			setSortOrder(SortOrder.DESC);
		} else {
			setSortOrder(SortOrder.ASC);
		}
	}

	async function getSalesReps() {
		setLoading(true);
		try {
			const response = await sendGETRequest("/client-orgs/get-sales-reps", {});

			const salesReps: Array<FrontendUserInfo> = response?.data?.salesReps;
			setSalesReps(salesReps);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	async function getClientOrganizations(searchText: string) {
		setLoading(true);
		try {
			const response = await sendGETRequest("/client-orgs/get-client-orgs", {
				searchText,
				sortBy,
				sortOrder,
				pageNumber: currentPagination,
				filterOption,
			});

			const organizations: Array<FrontendOrganization> =
				response?.data?.organizations;
			const pageCount: number = response?.data?.totalPages;
			setClientOrganizations(organizations);
			setTotalPageCount(pageCount);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}
	async function addOrganization(org: FrontendOrganization) {
		setAdminInAction(true);
		try {
			const response = await sendPOSTRequest("/client-orgs/add-organization", {
				organization: org,
			});

			openSnackbar(
				translation("NewOrganizationAdded"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
			goBackToViewOrganizationsPage(true);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setAdminInAction(false);
		}
	}

	const updateOrganization = (updatedOrg: FrontendOrganization) => {
		setClientOrganizations((prevOrg) =>
			prevOrg.map((org) =>
				org.organizationId === updatedOrg.organizationId
					? { ...org, ...updatedOrg }
					: org,
			),
		);
	};

	async function editOrganization(org: FrontendOrganization) {
		setAdminInAction(true);
		try {
			const response = await sendPOSTRequest("/client-orgs/edit-organization", {
				organization: org,
			});

			openSnackbar(
				translation("OrganizationInformationUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
			updateOrganization(org);
			setSelectedOrganization(org);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setAdminInAction(false);
		}
	}

	const filterSelectionOptions: Array<{
		key: string;
		name: string;
		description?: string;
		value: any;
		children?: any;
	}> = Object.values(ClientFilterOption).map((option: ClientFilterOption) => {
		const name: string = getClientFilterOptionString(option, translation);
		return {
			key: option,
			name: name,
			description: "",
			value: option,
			children: (
				<div
					className={`center-text status-selection-radio-option typography-body-02 ${
						filterOption === option
							? "status-selection-radio-option-selected"
							: ""
					}`}
				>
					{name}
				</div>
			),
		};
	});
	filterSelectionOptions.unshift({
		key: "All",
		name: translation("All"),
		description: "",
		value: "",
		children: (
			<div
				className={`center-text status-selection-radio-option ${
					filterOption === "" ? "status-selection-radio-option-selected" : ""
				}`}
			>
				{translation("All")}
			</div>
		),
	});

	const filterSelection: React.ReactNode = (
		<div
			style={{
				maxWidth: "700px",
				overflowX: "auto",
			}}
		>
			<CustomRadioGroup
				options={filterSelectionOptions}
				selectedOption={filterOption}
				onChange={(e) => {
					setFilterOption(e.target.value);
				}}
				className="status-selection-radio-group"
			/>
		</div>
	);

	const sortOptionSelection: React.ReactNode = (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "5px",
				marginLeft: "auto",
			}}
		>
			<div
				onClick={() => {
					toggleSortOrder();
				}}
				style={{ cursor: "pointer" }}
			>
				<SortArrowIcon sortOrder={sortOrder} />
			</div>
			<RowDropdown
				id="order-sort-order-dropdown"
				className="sort-order-dropdown"
				label={"a"}
				labelClassName="hide"
				options={Object.values(ClientOrganizationSortColumn).map((value) => {
					return {
						value: value,
						title: getClientOrganizationSortString(value, translation),
					};
				})}
				value={sortBy}
				setValue={setSortBy}
				noBorder
			></RowDropdown>
		</div>
	);

	useEffect(() => {
		getSalesReps();
	}, []);

	useEffect(() => {
		getClientOrganizations(searchText);
	}, [currentPagination, sortBy, sortOrder, filterOption]);

	function gotoOrganizationDetailsPage(org: FrontendOrganization) {
		setSelectedOrganization(org);
		setCurrentPageIndex(1);
	}
	function goBackToViewOrganizationsPage(reload: boolean) {
		setCurrentPageIndex(0);
		setSelectedOrganization(undefined);
		if (reload) {
			getClientOrganizations(searchText);
		}
	}
	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						minHeight: "90vh",
						marginTop: "60px",
					}}
				>
					<div
						style={{
							flex: "1",
							display: "flex",
							flexDirection: "column",
							gap: "20px",
						}}
					>
						<div className="view-orders-page-header">
							<div className="typography-heading-01">
								{translation("Organization")}
							</div>
							<CustomSearchBar
								id="view-order-page-searchbar"
								searchText={searchText}
								setSearchText={setSearchText}
								placeholder={translation("SearchByAssociationIdOrCompanyName")}
								searchButtonText={translation("Search")}
								onSearch={() => {
									if (currentPagination !== 1) {
										setCurrentPagination(1);
									} else {
										getClientOrganizations(searchText);
									}
								}}
							></CustomSearchBar>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: "10px",
							}}
						>
							{filterSelection}

							{sortOptionSelection}
						</div>

						<div style={{ display: "flex" }}>
							<BlackButton
								key="add-client-button"
								id="add-client-button"
								size="medium"
								children={
									<>
										<AddIcon></AddIcon>
										{translation("Organization")}
									</>
								}
								onClick={() => {
									setAddEditOrgModalOpen(true);
								}}
							></BlackButton>
						</div>

						{loading ? (
							<ViewOrderPageSkeletonUI />
						) : (
							<OrgsContent
								organizations={clientOrganizations}
								goToViewOrgDetails={gotoOrganizationDetailsPage}
								setDeleteOrgConfirmOpen={setDeleteOrgConfirmOpen}
								setProceedAction={setProceedAction}
							/>
						)}
					</div>
					{!loading && clientOrganizations.length > 0 && (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
							}}
						>
							<Pagination
								count={totalPageCount}
								page={currentPagination}
								onChange={(e, value) => {
									setCurrentPagination(value);
								}}
								sx={{
									backgroundColor: "white",
									borderRadius: "30px",
									padding: "6px 16px",

									"& .MuiPaginationItem-root": {
										backgroundColor: "white",
										color: "black",
										border: "1px solid transparent", // Hide outline when not selected
										"&:hover": {
											backgroundColor: "#f5f5f5", // Change background on hover
										},
										borderRadius: "50%",
									},
									"& .Mui-selected": {
										backgroundColor: SecondaryColor.SECONDARY_50, // Change background for selected item
										border: `1px solid ${NeutralColor.NEUTRAL_GREY_950}`, // Change text color for selected item
									},
								}}
								variant="outlined"
								shape="rounded"
							/>
						</div>
					)}
				</div>
			);
			break;
		case 1:
			currentComponent = (
				<OrgDetailsContent
					company={props.company}
					setCompany={props.setCompany}
					user={props.user}
					selectedOrganization={selectedOrganization as FrontendOrganization}
					goBackToViewOrganizationPage={goBackToViewOrganizationsPage}
					adminInAction={adminInAction}
					setAddEditOrgModalOpen={setAddEditOrgModalOpen} // setDeleteUserConfirmOpen={setDeleteUserConfirmOpen}
					setUnsavedChanges={setUnsavedChanges}
					gotoViewOrderPage={props.gotoViewOrderPage}
					// setProceedAction={setProceedAction}
				></OrgDetailsContent>
			);
			break;
	}

	return (
		<div style={{ margin: "0 auto" }}>
			{currentComponent}

			<AddOrEditOrganizationModal
				addOrEditOrganization={
					selectedOrganization ? editOrganization : addOrganization
				}
				open={addEditOrgModalOpen}
				setOpen={setAddEditOrgModalOpen}
				salesReps={salesReps}
				organization={selectedOrganization}
			></AddOrEditOrganizationModal>
			{/* <DiscardOrderConfirmModal
				open={discardOrderConfirmOpen}
				setOpen={setDiscardOrderConfirmOpen}
				proceedAction={proceedAction}
			></DiscardOrderConfirmModal> */}
		</div>
	);
}
